import jsPDF from 'jspdf';
import logoImage from '../assets/images/rg_logo_small.jpg';
import { RowData } from '../components/KaratCalculator';
import { removeDiacritics } from './removeDiacritics';

export interface MeltingRecordData {
    recordNumber?: string;
    date?: string;
    witness?: {
        name?: string;
        idSeries?: string;
        idNumber?: string;
    };
    administrator?: {
        name?: string;
        idSeries?: string;
        idNumber?: string;
    };
    location?: string;
    meltingData: RowData[];
    afterMass?: number;
    afterKarat?: number;
    observations?: string;
    totalPages?: number;
    totalAnnexes?: number;
    companyData?: {
        denumire?: string;
        cui?: string;
        j?: string;
        street?: string;
        iban?: string;
        banca?: string;
    };
    losses?: {
        lossGr?: number; // Loss in grams
        lossPercent?: number; // Loss percentage
        totalLossPercent?: number; // Total loss percentage
    };
}

function formatLosses(losses?: {
    lossGr?: number;
    lossPercent?: number;
    totalLossPercent?: number;
}): string {
    if (!losses) return '';
    const lines: string[] = [];
    if (typeof losses.lossGr === 'number') lines.push(`• Pierderi: ${losses.lossGr.toFixed(2)}g`);
    if (typeof losses.lossPercent === 'number')
        lines.push(`• Procent pierderi: ${losses.lossPercent.toFixed(2)}%`);
    if (typeof losses.totalLossPercent === 'number')
        lines.push(`• Procent total pierderi: ${losses.totalLossPercent.toFixed(2)}%`);
    return lines.length ? `\n\nPierderi în procesul de topire:\n${lines.join('\n')}` : '';
}

function buildMetalBreakdown(rows: RowData[]): string {
    return rows
        .map(row => {
            const quantity = row.quantityToMelt ?? row.grams ?? 0;
            const pmp = row.pmp ?? 0;
            const pmpText = pmp > 0 ? ` (PMP: ${pmp.toFixed(2)} lei/g)` : '';
            return `• ${row.metalType} ${row.karat}K: ${quantity.toFixed(2)}g${pmpText}`;
        })
        .join('\n');
}

function buildPmpSummary(
    avgTotalPmp: number,
    totalGoldGrams: number,
    avgGoldPmp: number,
    totalSilverGrams: number,
    avgSilverPmp: number,
): string {
    if (avgTotalPmp <= 0) return '';
    const parts: string[] = ['\n\nPMP (Preț Mediu Ponderat):'];
    if (totalGoldGrams > 0) parts.push(`• Aur: ${avgGoldPmp.toFixed(2)} lei/g`);
    if (totalSilverGrams > 0) parts.push(`• Argint: ${avgSilverPmp.toFixed(2)} lei/g`);
    parts.push(`• Total mediu ponderat: ${avgTotalPmp.toFixed(2)} lei/g`);
    return parts.join('\n');
}

function buildDetailedObservations(
    base: string | undefined,
    breakdown: string,
    totalGrams: number,
    afterMass?: number,
    afterKarat?: number,
    lossesText?: string,
): string {
    const lines: string[] = [];
    lines.push(base ?? 'Proces verbal pentru topirea materialelor de aur și argint.');
    lines.push('');
    lines.push('Detalii cantități topire:');
    lines.push(breakdown);
    lines.push('');
    lines.push(`Total cantitate: ${totalGrams.toFixed(2)}g`);
    if (typeof afterMass === 'number') lines.push(`Masă finală: ${afterMass.toFixed(2)}g`);
    if (typeof afterKarat === 'number') lines.push(`Puritate finală: ${afterKarat.toFixed(2)}K`);
    if (lossesText) lines.push(lossesText);
    return lines.join('\n');
}

export const generateMeltingRecordPdf = async (data: MeltingRecordData): Promise<void> => {
    try {
        // Calculate totals from melting data
        const goldRows = data.meltingData.filter(row => row.metalType === 'Gold');
        const silverRows = data.meltingData.filter(row => row.metalType === 'Silver');
        const totalGoldGrams = goldRows.reduce(
            (sum, row) => sum + (row.quantityToMelt ?? row.grams ?? 0),
            0,
        );
        const totalSilverGrams = silverRows.reduce(
            (sum, row) => sum + (row.quantityToMelt ?? row.grams ?? 0),
            0,
        );
        const totalGrams = totalGoldGrams + totalSilverGrams;

        // Calculate weighted average PMP values
        const goldPmpSum = goldRows.reduce((sum, row) => {
            const quantity = row.quantityToMelt ?? row.grams ?? 0;
            const pmp = row.pmp ?? 0;
            return sum + quantity * pmp;
        }, 0);
        const silverPmpSum = silverRows.reduce((sum, row) => {
            const quantity = row.quantityToMelt ?? row.grams ?? 0;
            const pmp = row.pmp ?? 0;
            return sum + quantity * pmp;
        }, 0);
        const avgGoldPmp = totalGoldGrams > 0 ? goldPmpSum / totalGoldGrams : 0;
        const avgSilverPmp = totalSilverGrams > 0 ? silverPmpSum / totalSilverGrams : 0;
        const totalPmpSum = goldPmpSum + silverPmpSum;
        const avgTotalPmp = totalGrams > 0 ? totalPmpSum / totalGrams : 0;

        // Create detailed metal breakdown
        const metalBreakdown = buildMetalBreakdown(data.meltingData);

        // Create losses section if available
        const lossesSection = formatLosses(data.losses);

        // Create detailed observations
        const detailedObservations = buildDetailedObservations(
            data.observations,
            metalBreakdown,
            totalGrams,
            data.afterMass,
            data.afterKarat,
            lossesSection,
        );

        // Create PDF
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        // Margins
        const leftMargin = 20;
        let y = 20;

        // Logo (top right)
        const logo = logoImage;
        pdf.addImage(logo, 'JPEG', 160, y, 30, 18); // x, y, width, height

        // ...existing code...

        // Company info (top left)
        const companyName = data.companyData?.denumire ?? 'REXO GOLD SRL';
        const companyCui = data.companyData?.cui ?? 'RO16758614';
        const companyJ = data.companyData?.j ?? 'J26/1529/2004';
        const companyStreet =
            data.companyData?.street ?? 'Str. Frantz Liszt 11, Targu Mures, Jud.: Mures';
        const companyIban = data.companyData?.iban ?? 'RO59RNCB0188034959830001';
        const companyBanca = data.companyData?.banca ?? 'BCR';

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(removeDiacritics(companyName), leftMargin, y + 6);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(removeDiacritics(`CIF: ${companyCui}`), leftMargin, y + 12);
        pdf.text(removeDiacritics(`Reg. com.: ${companyJ}`), leftMargin, y + 17);
        pdf.text(removeDiacritics(`Adresa: ${companyStreet}`), leftMargin, y + 22);
        pdf.text(removeDiacritics(`IBAN (RON): ${companyIban}`), leftMargin, y + 27);
        pdf.text(removeDiacritics(`Banca: ${companyBanca}`), leftMargin, y + 32);

        y += 40;

        // Title
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.text(
            removeDiacritics('Proces verbal de constatare si luare in evidenta'),
            leftMargin,
            y,
        );
        y += 14;

        // Record number and date
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(
            removeDiacritics(
                `Nr. ${data.recordNumber ?? ''} din ${data.date ?? new Date().toLocaleDateString('ro-RO')}`,
            ),
            leftMargin,
            y,
        );
        y += 10;

        // Parties section
        const witnessName = data.witness?.name ?? '';
        const witnessIdSeries = data.witness?.idSeries ?? '';
        const witnessIdNumber = data.witness?.idNumber ?? '';
        const adminName = data.administrator?.name ?? '';
        const adminIdSeries = data.administrator?.idSeries ?? '';
        const adminIdNumber = data.administrator?.idNumber ?? '';
        const location = data.location ?? companyStreet;
        const partiesText = `Incheiat azi ${data.date ?? new Date().toLocaleDateString('ro-RO')}, la sediul societatii ${location}, in prezenta lui ${witnessName}, act de identitate seria ${witnessIdSeries} nr. ${witnessIdNumber}, in calitate de asociat, si administratorul societatii ${adminName}, act de identitate seria ${adminIdSeries} nr. ${adminIdNumber}, reprezentand comisia de constatare.`;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        y = pdf
            .splitTextToSize(removeDiacritics(partiesText), 170)
            .reduce((accY: number, line: string) => {
                pdf.text(line, leftMargin, accY);
                return accY + 6;
            }, y);
        y += 2;

        // Metal summary section
        const pmpSummary = buildPmpSummary(
            avgTotalPmp,
            totalGoldGrams,
            avgGoldPmp,
            totalSilverGrams,
            avgSilverPmp,
        );

        const summaryText = `Prin prezentul proces verbal se ia in evidenta obiectele de aur reprezentand garantii cu titlu de amanet in favoarea SC ${companyName}, provenite de la diverse persoane fizice, pe baza contractelor incheiate intre societate si persoanele fizice conform anexei prezentului proces verbal.\n\nCantitatea totala de metal este de ${totalGrams.toFixed(2)}g, din care:\n• Aur: ${totalGoldGrams.toFixed(2)}g\n• Argint: ${totalSilverGrams.toFixed(2)}g${pmpSummary}`;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        y = pdf
            .splitTextToSize(removeDiacritics(summaryText), 170)
            .reduce((accY: number, line: string) => {
                pdf.text(line, leftMargin, accY);
                return accY + 6;
            }, y);
        y += 2;

        // Observations section
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(removeDiacritics('Constatari:'), leftMargin, y);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        y += 6;
        const obsLines = pdf.splitTextToSize(removeDiacritics(detailedObservations), 166);
        pdf.setDrawColor(0);
        pdf.setLineWidth(0.2);
        pdf.rect(leftMargin, y - 4, 170, obsLines.length * 6 + 8, 'S');
        obsLines.forEach((line: string, idx: number) => {
            pdf.text(line, leftMargin + 2, y + idx * 6);
        });
        y += obsLines.length * 6 + 12;

        // Footer section
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        const totalPages = data.totalPages?.toString() ?? '2';
        const totalAnnexes = data.totalAnnexes?.toString() ?? '1';
        const footerText = `Prezentul proces verbal, care contine ${totalPages} file si ${totalAnnexes} anexe, care fac parte integranta din cuprinsul acestuia, a fost incheiat azi ${data.date ?? new Date().toLocaleDateString('ro-RO')} in doua exemplare originale.`;
        y = pdf
            .splitTextToSize(removeDiacritics(footerText), 170)
            .reduce((accY: number, line: string) => {
                pdf.text(line, leftMargin, accY);
                return accY + 6;
            }, y);
        y += 10;

        // Signatures
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text('_____________', leftMargin, y);
        pdf.text('_____________', 170, y);
        pdf.text('Administrator', leftMargin, y + 6);
        pdf.text('L.S.', 170, y + 6);

        // Generate filename with current date and time
        const now = new Date();
        const filename = `Proces_Verbal_Topire_${now.getFullYear()}_${(now.getMonth() + 1)
            .toString()
            .padStart(2, '0')}_${now.getDate().toString().padStart(2, '0')}_${now
            .getHours()
            .toString()
            .padStart(2, '0')}_${now.getMinutes().toString().padStart(2, '0')}.pdf`;

        pdf.save(filename);
    } catch (error) {
        console.error('Error generating PDF:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new Error(`Failed to generate PDF document: ${errorMessage}`);
    }
};

export const showMeltingRecordModal = async (data: MeltingRecordData): Promise<void> => {
    try {
        // For now, we'll just generate and download the PDF
        // In a future enhancement, we could show it in a modal first
        await generateMeltingRecordPdf(data);
    } catch (error) {
        console.error('Error showing melting record:', error);
        throw error;
    }
};
